import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, TrendingUp, Loader2 } from 'lucide-react';
import TourCard from '../tour/TourCard';
import WhatsAppGroupButton from '../ui/WhatsAppGroupButton';
import { useTours } from '../../hooks/useTours';
import { useScrollReveal } from '../../hooks/useScrollReveal';

const FeaturedTours: React.FC = () => {
  const { ref, isVisible } = useScrollReveal();
  const { tours, loading, error } = useTours(6);
  
  // Get featured tours (only active tours)
  const featuredTours = tours.filter(tour => tour.status === 'active');

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.3,
      },
    },
  };

  return (
    <section ref={ref} className="py-2xl md:py-3xl gradient-cream-br">
      <div className="container">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isVisible ? "visible" : "hidden"}
          className="text-center mb-2xl"
        >
          {/* Section Header */}
          <motion.div
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-deep-blue bg-opacity-10 rounded-full mb-6"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isVisible ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.6 }}
          >
            <TrendingUp size={20} className="text-primary-deep-blue" />
            <span className="body-small font-semibold text-primary-deep-blue">
              Popular Destinations
            </span>
          </motion.div>

          <motion.h2
            className="heading-primary text-neutral-charcoal mb-6"
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Upcoming Tours & Adventures
          </motion.h2>

          <motion.p
            className="body-large text-secondary-slate max-w-3xl mx-auto mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            Embark on extraordinary journeys to breathtaking destinations. 
            Our carefully curated tours offer the perfect blend of adventure, 
            culture, and luxury for the modern traveler.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-2xl"
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <Link
              to="/tours"
              className="btn btn-primary group"
            >
              View All Tours
              <ChevronRight 
                size={20} 
                className="group-hover:translate-x-1 transition-transform" 
              />
            </Link>
            <WhatsAppGroupButton />
          </motion.div>
        </motion.div>

        {/* Tours Grid */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8"
          variants={containerVariants}
          initial="hidden"
          animate={isVisible ? "visible" : "hidden"}
        >
          {loading ? (
            // Loading state
            <div className="col-span-full flex items-center justify-center py-12">
              <div className="flex items-center gap-3 text-secondary-slate">
                <Loader2 size={24} className="animate-spin" />
                <span className="body-large">Loading tours...</span>
              </div>
            </div>
          ) : error || featuredTours.length === 0 ? (
            <div className="col-span-full flex items-center justify-center py-12">
              <div className="text-center">
                <h3 className="heading-tertiary text-secondary-slate mb-2">No Tours Available</h3>
                <p className="body-large text-secondary-slate">
                  Check back later for upcoming tours and adventures.
                </p>
              </div>
            </div>
          ) : (
            // Tours display
            featuredTours.map((tour, index) => (
              <motion.div
                key={tour.tourId}
                initial={{ opacity: 0, y: 50 }}
                animate={isVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ 
                  duration: 0.6, 
                  delay: 0.8 + index * 0.1,
                  ease: 'easeOut' 
                }}
              >
                <TourCard tour={tour} index={index} />
              </motion.div>
            ))
          )}
        </motion.div>

      </div>
    </section>
  );
};

export default FeaturedTours;
